import { last } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OptionalValmetDnaConfiguredTag, ValmetDnaSubtitle, isEmptyValmetDnaConfiguredTag, isSubtitle } from '~app/shared/map/layer/layer-definitions/T14';
import { arraysEqual } from '~app/shared/utils';
import { getFullTag } from './measurements/neve-measurements-tag.service';
import { TagpairState } from './neve-measurements-table.component';
import { ValmetDnaTag } from './valmet-dna.service';

export type GroupedColumnConfig = {
  name: string,
  valmetDnaTags: OptionalValmetDnaConfiguredTag[]
};

type GroupedColumn = {
  isEmpty: false
  value: {
    formatted: string,
    numeric: number,
    ts: string
  },
  tag: ValmetDnaTag
  requestedFullTag: string
} | {
  /**
   * No tag was specified
   */
  isEmpty: true
};

type TableRowGrouped = {
  name: string
  columns: GroupedColumn[]
};

export type TableDataGrouped = {
  tableType: 'grouped'
  columnNames: string[],
  rows: TableRowGrouped[]
};

type ColumnWithTagpairStates = {
  name: string
  /** can have undefined entries for now! */
  tagpairStates: TagpairState[]
};

export const createGroupedUnsortedRows = (tagpairStates: TagpairState[], rowNames: (string | ValmetDnaSubtitle)[], columnConfigs: GroupedColumnConfig[]) => {
  // TODO:
  if (!rowNames) {
    throw new Error('automatic grouping not yet implemented');
  }

  const columnsWithValues = columnConfigs.map(column => ({
      name: column.name,
      tagpairStates: column.valmetDnaTags.map(tag => {
        if (!isEmptyValmetDnaConfiguredTag(tag)) {
          return tagpairStates.find(tps => arraysEqual(tps.tagpair, tag.tagpair));
        }
        return undefined;
      })
    })
  );

  // Subtitles don't have corresponding entries in the tag lists,
  // so need to track the tag index separately.
  let index = 0;
  const rows = rowNames.map((rowName) => {
    if (isSubtitle(rowName)) {
      return rowName;
    }
    return getTableRowGrouped({
      name: rowName,
      index: index++
    }, columnsWithValues);
  });

  return {
    unsortedRows: rows,
    tagpairStates
  };
};

export const createTableDataGrouped = (sortedRows$: Observable<{
  tagpairStates: TagpairState[];
  sortedRows: TableRowGrouped[];
}>, columnNames: string[]) => {
  return sortedRows$.pipe(
    map(obj => ({
        tableType: 'grouped',
        columnNames,
        rows: obj.sortedRows,
      } as TableDataGrouped
    ))
  );
};

export const getTableRowGrouped = (row: {
  name: string,
  index: number
},
  columnsWithValues: ColumnWithTagpairStates[]): TableRowGrouped => {
  const columnValuesForThisRow: {
    name: string,
    value?: TagpairState
  }[] = columnsWithValues.map(col => ({
    name: col.name,
    value: col.tagpairStates.find((val, index) => index === row.index)
  })
  );

  const columns: GroupedColumn[] = columnValuesForThisRow.map(col => {
    if (!col.value) {
      return {
        isEmpty: true
      };
    }
    const { state } = col.value;
    const commonProperties = {
      tag: state.metadata.tag,
      requestedFullTag: getFullTag(col.value.config.tagpair),
      isEmpty: false
    };

    if (!state.transformedMeasurements) {
      return {
        value: undefined,
        ...commonProperties
      };
    }
    const lastTrendValue = last(state.transformedMeasurements);

    return {
      value: {
        formatted: lastTrendValue?.numericValue?.toFixed(1),
        numeric: lastTrendValue?.numericValue,
        ts: lastTrendValue.ts,
      },
      ...commonProperties
    };
  });

  return {
    name: row.name,
    columns
  };
};
